import Button from '@watershed/ui-core/components/Button';
import { Dialog } from '@watershed/ui-core/components/Dialog';
import FileUploadDropzone, {
  RejectedFile,
  UploadFile,
} from '@watershed/ui-core/components/FileUpload/FileUploadDropzone';
import { Trans } from '@lingui/react/macro';
import some from 'lodash/some';
import { useEffect, useState } from 'react';
import { getGqlResultDataBang } from '@watershed/shared-frontend/utils/errorUtils';
import {
  is403Error,
  isResponseOk,
  uploadWithRetries,
} from '@watershed/shared-frontend/utils/requestUtils';
import useSnackbar from '@watershed/shared-frontend/hooks/useSnackbar';
import Callout from '@watershed/shared-frontend/components/Callout';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import useManageSupportCaseAttachments from './useManageSupportCaseAttachments';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export default function SupportCaseAttachmentDialog({
  open,
  onClose,
  onSubmit,
  files,
  getDownloadFileUrl,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (files: Array<UploadFile>) => void;
  files: Array<UploadFile>;
  removeUpload: (fileId: string) => void;
  getDownloadFileUrl: (fileMetadataId: string) => Promise<string | undefined>;
}) {
  const { executeCreateSupportCaseAttachments, executeDeleteAttachmentItems } =
    useManageSupportCaseAttachments({
      files,
      // Note: This dialog has its own temporary stash of "attached files"
      // and it calls the equivalent of setFiles on submit
      setFiles: () => {},
    });
  const [attachedFiles, setAttachedFiles] = useState<Array<UploadFile>>(files);
  const [fileUploadProgressMap, setFileUploadProgressMap] = useState<{
    [fileMetadataId: string]: {
      progress: number;
    };
  }>({});
  const [errors, setErrors] = useState<Array<string>>([]);

  const snackbar = useSnackbar();

  // Sync dialog state with parent state when dialog opens
  useEffect(() => {
    if (open) {
      setAttachedFiles(files); // Reset dialog files to match parent
    }
  }, [open, files]);

  const removeUpload = async (fileId: string) => {
    const item = attachedFiles.find((file) => file.fileId === fileId);
    if (!item) {
      return;
    }
    await executeDeleteAttachmentItems({
      input: { fileMetadataIds: [fileId] },
    });
    setAttachedFiles(attachedFiles.filter((file) => file.fileId !== fileId));
  };

  const onDrop = async (acceptedFiles: Array<File>) => {
    if (acceptedFiles.length === 0) {
      return;
    }
    const fileInfo = acceptedFiles.map((file) => {
      return {
        fileName: file.name,
        sizeBytes: file.size,
      };
    });

    const result = await executeCreateSupportCaseAttachments({
      input: {
        files: fileInfo,
      },
    });

    const data = getGqlResultDataBang(result);
    const attachmentResponseFiles = data.createSupportCaseAttachments.files;

    // Create upload promises directly (don't await yet)
    const uploadPromises = attachmentResponseFiles.map(
      async ({ fileMetadataId, signedUrl }, i) => {
        return uploadWithRetries(signedUrl, acceptedFiles[i], (progress) =>
          setFileUploadProgressMap((prev) => ({
            ...prev,
            [fileMetadataId]: { progress },
          }))
        );
      }
    );

    // set the files before we start uploading so we can track their progress
    setAttachedFiles((prev) => [
      ...prev,
      ...attachmentResponseFiles.map((file) => {
        return {
          fileId: file.fileMetadataId,
          isImported: false,
          filename: file.fileName,
        };
      }),
    ]);

    // Now await all uploads
    const responses = await Promise.all(uploadPromises);
    const failedFileIds = await Promise.all(
      responses.map(async (response: XMLHttpRequest, index: number) => {
        const fileMetadataId = attachmentResponseFiles[index].fileMetadataId;
        const fileName = acceptedFiles[index].name;
        if (!isResponseOk(response)) {
          let errorMessage = (
            <Trans context="file upload error">
              There was an error uploading "{fileName}".
            </Trans>
          );
          if (is403Error(response)) {
            errorMessage = (
              <Trans>
                There was an error uploading "{fileName}". Please ask your IT
                admin to whitelist "storage.googleapis.com" in your
                firewall/proxy settings.
              </Trans>
            );
          }
          snackbar.enqueueSnackbar(errorMessage, { variant: 'error' });

          // Clean up backend - don't let cleanup failures block the main flow
          try {
            await executeDeleteAttachmentItems({
              input: {
                fileMetadataIds: [fileMetadataId],
              },
            });
          } catch (cleanupError) {
            // Log cleanup error but don't propagate it - the upload already failed,
            // so we still want to mark this file as failed and update UI state
            console.error(
              'Failed to clean up attachment item after upload failure',
              {
                fileMetadataId,
                fileName,
                error: cleanupError,
              }
            );
          }

          return fileMetadataId;
        }
        return null;
      })
    ).then((results) => results.filter((id): id is string => id !== null));

    // Mark successful uploads as imported and remove failed ones
    setAttachedFiles((prev) =>
      prev
        .filter((file) => !failedFileIds.includes(file.fileId))
        .map((file) => {
          const wasJustUploaded = attachmentResponseFiles.some(
            (f) => f.fileMetadataId === file.fileId
          );
          if (wasJustUploaded) {
            return { ...file, isImported: true };
          }
          return file;
        })
    );

    // Clean up progress map for failed uploads
    if (failedFileIds.length > 0) {
      setFileUploadProgressMap((prev) => {
        const updated = { ...prev };
        for (const fileId of failedFileIds) {
          delete updated[fileId];
        }
        return updated;
      });
    }
  };

  const onDropRejected = (rejectedFiles: Array<RejectedFile>) => {
    setErrors((prev) => [
      ...prev,
      ...rejectedFiles.map((file) => file.message),
    ]);
  };

  const getDownloadUrl = (file: UploadFile) => {
    return getDownloadFileUrl(file.fileId);
  };

  return (
    <Dialog
      header={{
        title: (
          <Trans context="button to attach files to comment">
            Attach files
          </Trans>
        ),
      }}
      actions={
        <>
          <Button
            onClick={() => {
              setErrors([]);
              onClose();
            }}
          >
            <Trans context="button to cancel uploads">Cancel upload</Trans>
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setErrors([]);
              onSubmit(attachedFiles);
            }}
            disabled={
              attachedFiles.length === 0 ||
              some(attachedFiles.map((file) => !file.isImported))
            }
            data-testid={TestIds.SupportAttachmentSubmitButton}
          >
            <Trans context="button to attach files to comment">
              Attach files
            </Trans>
          </Button>
        </>
      }
      onClose={() => {
        setErrors([]);
        onClose();
      }}
      open={open}
    >
      <FileUploadDropzone
        files={attachedFiles}
        onDropAccepted={onDrop}
        onDropRejected={onDropRejected}
        fileUploadProgressMap={fileUploadProgressMap}
        removeUpload={removeUpload}
        maxSizeBytes={MAX_FILE_SIZE_BYTES}
        additionalUsageText={
          <Trans context="max file size">Maximum file size is 10MB</Trans>
        }
        getDownloadUrl={getDownloadUrl}
      />
      {errors.map((error, i) => (
        <Callout
          key={i}
          isDense
          variant="error"
          description={error}
          onDismiss={() =>
            setErrors((prev) => prev.filter((_, errIndex) => i !== errIndex))
          }
        />
      ))}
    </Dialog>
  );
}
